// Premium customer-facing quote proposal — shared by the public quote link
// (/q/:token), the in-app Preview and print. Presentation only: every number
// comes from the server payload (see backend/quote_proposal.py).
import "./quoteProposal.css";
import { Fragment, useMemo, useState } from "react";
import {
  FileText, Calendar, Tag, User, Hammer, ClipboardList, Check, X, PenLine,
  CreditCard, MessageSquare, Linkedin, Facebook, Instagram, ShieldCheck, Clock,
  Package, Truck, Wrench,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export const imgSrc = (u) => (u && u.startsWith("/api") ? `${BACKEND}${u}` : u);

export const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const qty = (n) => `${Number(n || 0)}`;

const rgba = (hex, a) => {
  const h = (hex || "#FF5F15").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

export const accentInk = (hex) => {
  const channels = (hex || "#FF5F15").replace("#", "").match(/.{2}/g);
  if (!channels || channels.length !== 3) return "#111318";
  const rgb = channels.map((c) => parseInt(c, 16) / 255).map((c) => c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2] > 0.179 ? "#111318" : "#ffffff";
};

const STATUS_LABEL = { open: "Open", accepted: "Accepted", declined: "Declined", expired: "Expired" };

// ---------- small building blocks ----------

const PanelHead = ({ icon: Icon, title, accent, testid }) => (
  <div
    className={`flex items-center gap-3 px-4 sm:px-6 py-3.5 proposal-heading-surface`}
    style={{ borderLeft: `3px solid ${accent}` }}
    data-testid={testid}>
    <span className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: accent, color: "var(--proposal-on-accent)" }}>
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </span>
    <h2 className={`text-sm sm:text-base font-black uppercase tracking-[0.08em] proposal-text`}>
      {title}
    </h2>
  </div>
);

const Blocks = ({ blocks, html, accent, testid }) => {
  if (html) {
    return (
      <div
        className="proposal-rich-text prose prose-sm max-w-none proposal-body [&_ul]:list-none [&_ul]:pl-0 [&_li]:relative [&_li]:pl-5 [&_li]:mb-1.5 [&_p]:mb-3 [&_a]:underline"
        style={{ ["--tw-prose-bullets"]: accent }}
        data-testid={testid}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return (
    <div className="space-y-3" data-testid={testid}>
      {(blocks || []).map((b, i) =>
        b.kind === "bullets" ? (
          <ul key={i} className="space-y-1.5">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2.5 text-[13.5px] sm:text-sm leading-relaxed proposal-body">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent, color: "var(--proposal-on-accent)" }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p key={i} className="text-[13.5px] sm:text-sm leading-relaxed proposal-body">{b.text}</p>
        )
      )}
    </div>
  );
};

// ---------- hero ----------

const Hero = ({ doc, accent }) => {
  const org = doc.org || {};
  const cover = imgSrc(org.cover_url);
  const status = doc.effective_status || "open";
  return (
    <header
      className="proposal-hero relative overflow-hidden proposal-heading-surface px-5 sm:px-8 py-7 sm:py-8"
      data-testid="proposal-hero">
      {cover && (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${cover})` }} aria-hidden />
      )}
      <div className="absolute inset-0" style={{ background: "linear-gradient(100deg,rgba(9,9,11,0.94) 0%,rgba(9,9,11,0.80) 45%,rgba(9,9,11,0.72) 100%)" }} aria-hidden />
      <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-4">
            {org.logo_url && (
              <img src={imgSrc(org.logo_url)} alt={`${org.name} logo`} onError={event => { event.currentTarget.style.display = "none"; }}
                style={org.logo_background ? { background: org.logo_background, padding: 4, borderRadius: 4 } : undefined}
                className="h-11 sm:h-14 w-auto max-w-[150px] object-contain shrink-0" data-testid="proposal-logo" />
            )}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-black uppercase tracking-tight text-white leading-none break-words"
                data-testid="proposal-org-name">
                {org.name}
              </h1>
              {org.tagline && (
                <div className="mt-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] proposal-muted"
                  data-testid="proposal-org-tagline">
                  {org.tagline}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="md:text-right shrink-0" data-testid="proposal-hero-meta">
          <div className="text-base sm:text-xl font-black uppercase tracking-tight text-white">
            {doc.label} {doc.quote_number}
          </div>
          <dl className="mt-2 space-y-1 text-[11px] sm:text-xs proposal-muted">
            {doc.issue_date && (
              <div className="flex md:justify-end gap-2"><dt className="proposal-muted">Issued:</dt><dd className="font-semibold">{doc.issue_date}</dd></div>
            )}
            {doc.valid_until && (
              <div className="flex md:justify-end gap-2"><dt className="proposal-muted">Valid until:</dt><dd className="font-semibold">{doc.valid_until}</dd></div>
            )}
            <div className="flex md:justify-end items-center gap-2 pt-0.5">
              <dt className="proposal-muted">Status:</dt>
              <dd>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white"
                  style={{ color: status === "open" ? "var(--proposal-on-accent)" : "#fff", background: status === "declined" ? "#DC2626" : status === "expired" ? "#52525B" : status === "accepted" ? "#059669" : accent }}
                  data-testid="proposal-status">
                  {status === "accepted" ? <Check className="w-3 h-3" strokeWidth={3} /> : status === "declined" ? <X className="w-3 h-3" strokeWidth={3} /> : status === "expired" ? <Clock className="w-3 h-3" strokeWidth={3} /> : null}
                  {STATUS_LABEL[status] || status}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </header>
  );
};

// ---------- pricing ----------

const CATEGORY_ICON = { labour: Hammer, material: Package, plant: Truck, service: Wrench };

const ItemThumb = ({ item, accent, show }) => {
  if (!show) return null;
  if (item.image_url) {
    return (
      <img src={imgSrc(item.image_url)} alt="" aria-hidden
        className="w-12 h-12 object-cover border proposal-border shrink-0" />
    );
  }
  const Icon = CATEGORY_ICON[item.category];
  if (!Icon) return null;
  return (
    <span className="w-12 h-12 hidden sm:flex items-center justify-center border proposal-border proposal-surface shrink-0" aria-hidden>
      <Icon className="w-4 h-4" style={{ color: "var(--proposal-accent-text)" }} strokeWidth={2.2} />
    </span>
  );
};

const PricingTable = ({ doc, accent }) => {
  const d = doc.display || {};
  const mode = d.pricing_display || "detailed";
  const showQty = mode === "detailed" && d.show_quantities !== false;
  const showRate = mode === "detailed" && d.show_unit_prices !== false;
  const showDesc = d.show_descriptions !== false;
  const showImages = d.show_item_images !== false;
  const sections = doc.sections || [];
  const named = sections.filter((s) => s.title).length > 0;

  if (mode === "lump_sum") {
    return (
      <div className="px-5 sm:px-8 py-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
        style={{ background: rgba(accent, 0.07) }} data-testid="pricing-lump-sum">
        <div className="text-[11px] font-black uppercase tracking-[0.2em] proposal-muted">Project total</div>
        <div className="text-3xl sm:text-4xl font-black tracking-tight proposal-text" data-testid="lump-sum-total">
          {money(doc.totals?.total)}
        </div>
      </div>
    );
  }

  if (mode === "section_summary") {
    const rows = sections.filter((s) => (s.items || []).length);
    return (
      <div data-testid="pricing-section-summary">
        {rows.map((s, i) => (
          <div key={i} className="flex items-center justify-between gap-4 px-5 sm:px-8 py-4 border-b proposal-border last:border-b-0">
            <div className="min-w-0">
              <div className="text-sm font-black uppercase tracking-wide proposal-text">{s.title || "Works"}</div>
              {s.detail && <div className="text-xs proposal-muted mt-0.5">{s.detail}</div>}
            </div>
            <div className="font-black text-sm sm:text-base tabular-nums shrink-0">{money(s.subtotal)}</div>
          </div>
        ))}
      </div>
    );
  }

  const cols = showQty && showRate ? 5 : showQty ? 4 : 2;

  return (
    <div data-testid="pricing-detailed">
      {/* desktop / tablet table */}
      <table className="hidden md:table w-full border-collapse" data-testid="pricing-table">
        <thead>
          <tr className="proposal-heading-surface text-[10px] font-bold uppercase tracking-[0.14em] proposal-muted">
            <th scope="col" className="text-left px-6 py-2.5 font-bold">Description</th>
            {showQty && <th scope="col" className="text-right px-3 py-2.5 font-bold w-[70px]">Qty</th>}
            {showQty && <th scope="col" className="text-right px-3 py-2.5 font-bold w-[70px]">Unit</th>}
            {showRate && <th scope="col" className="text-right px-3 py-2.5 font-bold w-[120px]">Rate (NZD)</th>}
            <th scope="col" className="text-right px-6 py-2.5 font-bold w-[130px]">Total (NZD)</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((s, si) => (
            <Fragment key={si}>
              {s.title && named && (
                <tr className="proposal-heading-surface">
                  <td colSpan={cols} className="px-6 py-2.5">
                    <div className="text-xs font-black uppercase tracking-[0.12em] text-white">{s.title}</div>
                    {s.detail && <div className="text-[11px] proposal-muted mt-0.5">{s.detail}</div>}
                  </td>
                </tr>
              )}
              {(s.items || []).map((li, i) => (
                <tr key={i} className="border-b proposal-border align-middle proposal-row transition-colors">
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3.5">
                      <ItemThumb item={li} accent={accent} show={showImages} />
                      <div className="min-w-0">
                        <div className="text-sm font-bold proposal-text">
                          {li.item_no && <span className="proposal-muted font-mono text-xs mr-1.5">{li.item_no}</span>}
                          {li.description}
                        </div>
                        {showDesc && li.detail && <div className="text-xs proposal-muted mt-0.5">{li.detail}</div>}
                        {li.discount_pct && d.show_discount !== false ? (
                          <div className="text-[11px] font-bold mt-0.5" style={{ color: "var(--proposal-accent-text)" }}>Less {li.discount_pct}%</div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  {showQty && <td className="px-3 py-3.5 text-right text-sm tabular-nums proposal-body">{qty(li.qty)}</td>}
                  {showQty && <td className="px-3 py-3.5 text-right text-sm proposal-body">{li.unit || ""}</td>}
                  {showRate && <td className="px-3 py-3.5 text-right text-sm tabular-nums proposal-body">{money(li.unit_price)}</td>}
                  <td className="px-6 py-3.5 text-right text-sm font-black tabular-nums proposal-text">{money(li.total)}</td>
                </tr>
              ))}
              {s.title && named && d.show_section_subtotals !== false && (
                <tr className="proposal-surface border-b proposal-border">
                  <td colSpan={cols - 1} className="px-6 py-2.5 text-[10px] font-black uppercase tracking-[0.16em] proposal-muted">
                    Section subtotal
                  </td>
                  <td className="px-6 py-2.5 text-right text-sm font-black tabular-nums">{money(s.subtotal)}</td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      {/* mobile cards */}
      <div className="md:hidden divide-y divide-zinc-100" data-testid="pricing-cards-mobile">
        {sections.map((s, si) => (
          <div key={si}>
            {s.title && named && (
              <div className="proposal-heading-surface px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.12em] text-white">{s.title}</div>
            )}
            {(s.items || []).map((li, i) => (
              <div key={i} className="px-5 py-4 border-b proposal-border">
                <div className="flex items-start gap-3">
                  {showImages && li.image_url && (
                    <img src={imgSrc(li.image_url)} alt="" aria-hidden className="w-11 h-11 object-cover border proposal-border shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-black uppercase tracking-wide proposal-text">{li.description}</div>
                    {showDesc && li.detail && <div className="text-xs proposal-muted mt-0.5">{li.detail}</div>}
                    {showQty && (
                      <div className="text-xs proposal-muted mt-1.5 tabular-nums">
                        {qty(li.qty)} {li.unit || ""}{showRate ? ` × ${money(li.unit_price)}` : ""}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2.5 flex items-center justify-between border-t border-dashed proposal-border pt-2.5">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] proposal-muted">Total</span>
                  <span className="text-base font-black tabular-nums">{money(li.total)}</span>
                </div>
              </div>
            ))}
            {s.title && named && d.show_section_subtotals !== false && (
              <div className="flex items-center justify-between px-5 py-3 proposal-surface">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] proposal-muted">Section subtotal</span>
                <span className="text-sm font-black tabular-nums">{money(s.subtotal)}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const TotalsPanel = ({ doc, accent }) => {
  const t = doc.totals || {};
  const d = doc.display || {};
  return (
    <div className="px-5 sm:px-8 pt-6 pb-2 flex justify-end" data-testid="proposal-totals">
      <div className="w-full sm:max-w-[420px] px-5 sm:px-6 py-5" style={{ background: rgba(accent, 0.08) }}>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="proposal-body">Subtotal{t.gst_enabled !== false ? ` (${t.gst_inclusive ? "incl." : "excl."} GST)` : ""}</dt><dd className="tabular-nums font-semibold">{money(t.subtotal)}</dd></div>
          {Number(t.discount) > 0 && d.show_discount !== false && (
            <div className="flex justify-between"><dt className="proposal-body">Discount</dt><dd className="tabular-nums font-semibold">-{money(t.discount)}</dd></div>
          )}
          {t.gst_enabled !== false && !t.gst_inclusive && Number(t.discount) > 0 && <div className="flex justify-between"><dt className="proposal-body">{Number(t.discount) > 0 && d.show_discount !== false ? "After discount" : "Subtotal"} (excl. GST)</dt><dd className="tabular-nums font-semibold">{money(Number(t.total || 0) - Number(t.gst || 0))}</dd></div>}
          {t.gst_enabled !== false && (
          <div className="flex justify-between">
            <dt className="proposal-body">{t.gst_inclusive ? "GST included" : "GST"} ({Number(t.gst_rate_pct || 15)}%)</dt>
            <dd className="tabular-nums font-semibold">{money(t.gst)}</dd>
          </div>
          )}
          {t.gst_enabled !== false && t.gst_inclusive && <div className="flex justify-between"><dt className="proposal-body">{Number(t.discount) > 0 && d.show_discount !== false ? "After discount" : "Subtotal"} (excl. GST)</dt><dd className="tabular-nums font-semibold">{money(Number(t.total || 0) - Number(t.gst || 0))}</dd></div>}
        </dl>
        <div className="mt-4 pt-4 border-t proposal-border/70 flex items-end justify-between gap-4">
          <span className="text-base sm:text-lg font-black tracking-tight proposal-text">Total (incl. GST)</span>
          <span className="text-2xl sm:text-3xl font-black tracking-tight tabular-nums proposal-text" data-testid="proposal-total">
            {money(t.total)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------- acceptance ----------

const AcceptancePanel = ({ doc, accent, onAccept, onDecline, busy, preview }) => {
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [termsOk, setTermsOk] = useState(false);
  const termsRequired = !!(doc.terms_blocks || []).length;
  const canAccept = name.trim().length >= 2 && (!termsRequired || termsOk);

  return (
    <section className="proposal-heading-surface px-5 sm:px-8 py-7 print:hidden" data-testid="proposal-acceptance">
      <div className="flex items-start gap-3.5">
        <span className="w-8 h-8 flex items-center justify-center shrink-0" style={{ background: accent, color: "var(--proposal-on-accent)" }}>
          <PenLine className="w-4 h-4" strokeWidth={2.4} />
        </span>
        <div>
          <h2 className="text-base sm:text-lg font-black uppercase tracking-tight text-white">Ready to go ahead?</h2>
          <p className="text-xs proposal-muted mt-1 max-w-xl">
            Accept this {doc.label?.toLowerCase()} to get things underway. Your name, time and IP address will be recorded.
          </p>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="accept-name" className="block text-[11px] font-bold proposal-muted mb-1.5">
            Your full name (acts as your signature) <span style={{ color: "var(--proposal-accent-text)" }}>*</span>
          </label>
          <input id="accept-name" value={name} onChange={(e) => setName(e.target.value)} disabled={preview || busy}
            placeholder="e.g. Jordan Smith" autoComplete="name"
            className="w-full proposal-input border px-3.5 py-3 text-sm outline-none disabled:opacity-60"
            data-testid="public-quote-name" />
        </div>
        <div>
          <label htmlFor="accept-comment" className="block text-[11px] font-bold proposal-muted mb-1.5">Optional comment</label>
          <input id="accept-comment" value={comment} onChange={(e) => setComment(e.target.value)} disabled={preview || busy}
            placeholder="Add a message (optional)…"
            className="w-full proposal-input border px-3.5 py-3 text-sm outline-none disabled:opacity-60"
            data-testid="public-quote-comment" />
        </div>
      </div>

      {termsRequired && (
        <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none text-xs proposal-muted">
          <input type="checkbox" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} disabled={preview || busy}
            className="mt-[2px] w-4 h-4 accent-current" style={{ accentColor: accent }}
            data-testid="public-quote-terms-checkbox" />
          <span>I accept the terms &amp; conditions above <span style={{ color: "var(--proposal-accent-text)" }}>*</span></span>
        </label>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button type="button" onClick={() => onAccept({ name: name.trim(), comment: comment.trim(), termsOk })}
          disabled={preview || busy || !canAccept}
          className="flex-1 sm:flex-none sm:min-w-[260px] inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-black uppercase tracking-[0.12em] text-white transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-45 disabled:hover:translate-y-0"
          style={{ background: accent, color: "var(--proposal-on-accent)" }}
          data-testid="public-quote-accept">
          <Check className="w-4 h-4" strokeWidth={3} /> Accept {doc.label?.toLowerCase()}
        </button>
        <button type="button" onClick={onDecline} disabled={preview || busy}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-black uppercase tracking-[0.12em] proposal-muted border border-white/25 hover:border-white/60 hover:text-white transition-colors disabled:opacity-45"
          data-testid="public-quote-decline">
          <X className="w-4 h-4" strokeWidth={3} /> Decline
        </button>
      </div>
      {preview && (
        <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--proposal-accent-text)" }} data-testid="proposal-preview-note">
          Preview only — this is exactly what your customer sees
        </p>
      )}
    </section>
  );
};

const ClosedPanel = ({ doc, accent }) => {
  const st = doc.effective_status;
  const acc = doc.acceptance || {};
  const at = acc.at ? new Date(acc.at) : null;
  const org = doc.org || {};
  const tone = st === "accepted" ? "#059669" : st === "declined" ? "#DC2626" : "#52525B";
  const Icon = st === "accepted" ? ShieldCheck : st === "declined" ? X : Clock;
  return (
    <section className="proposal-heading-surface px-5 sm:px-8 py-8" data-testid="proposal-closed">
      <div className="flex items-start gap-4">
        <span className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: tone }}>
          <Icon className="w-5 h-5 text-white" strokeWidth={2.4} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight text-white" data-testid="proposal-closed-title">
            {st === "accepted" ? `${doc.label} accepted` : st === "declined" ? `${doc.label} declined` : `${doc.label} expired`}
          </h2>
          {st === "accepted" && (
            <p className="text-sm proposal-muted mt-2">
              {acc.name ? <>Thanks {acc.name}. </> : null}
              This {doc.label?.toLowerCase()} was accepted
              {at ? <> on <b className="text-white">{at.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}</b> at <b className="text-white">{at.toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" })}</b></> : null}.
            </p>
          )}
          {st === "declined" && (
            <p className="text-sm proposal-muted mt-2">
              This {doc.label?.toLowerCase()} was declined
              {at ? <> on <b className="text-white">{at.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}</b></> : null}.
              {doc.declined_reason ? <> Reason: <b className="text-white">{doc.declined_reason}</b></> : null}
            </p>
          )}
          {st === "expired" && (
            <p className="text-sm proposal-muted mt-2">
              This {doc.label?.toLowerCase()} expired on <b className="text-white">{doc.valid_until}</b>.
              Please contact <b className="text-white">{org.name}</b> to request an updated {doc.label?.toLowerCase()}.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[11px] font-bold uppercase tracking-[0.16em] proposal-muted">
            <span>Reference: <span style={{ color: "var(--proposal-accent-text)" }}>{doc.quote_number}</span></span>
            {st === "accepted" && <span className="proposal-muted">{org.name} has been notified</span>}
            {(org.phone || org.email) && <span className="normal-case tracking-normal font-medium proposal-muted">{org.phone || org.email}</span>}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- main ----------

export default function QuoteProposal({ doc, onAccept, onDecline, busy = false, preview = false }) {
  const accent = doc?.accent || "#FF5F15";
  const org = doc?.org || {};
  const client = doc?.client || {};
  const [showAllTerms, setShowAllTerms] = useState(false);
  const details = useMemo(() => ([
    { icon: FileText, label: `${doc.label} Number`, value: doc.quote_number },
    { icon: Calendar, label: "Issue Date", value: doc.issue_date },
    { icon: Calendar, label: "Valid Until", value: doc.valid_until ? `${doc.valid_until}${doc.valid_days ? ` (${doc.valid_days} days)` : ""}` : null },
    { icon: Tag, label: "Reference", value: doc.reference },
  ].filter((r) => r.value)), [doc]);

  const terms = doc.terms_blocks || [];
  const termsCount = terms.reduce((n, b) => n + (b.kind === "bullets" ? b.items.length : 1), 0);
  const longTerms = termsCount > 8;
  const visibleTerms = longTerms && !showAllTerms ? terms.slice(0, 3) : terms;
  const closed = ["accepted", "declined", "expired"].includes(doc.effective_status);

  return (
    <div className="quote-proposal-theme min-h-screen proposal-canvas py-0 sm:py-8 lg:py-10 px-0 sm:px-5 print:py-0" style={{ "--proposal-accent": accent, "--proposal-on-accent": accentInk(accent) }}>
      <article
        className="mx-auto w-full max-w-[1180px] proposal-document shadow-[0_20px_60px_-20px_rgba(9,9,11,0.28)] print:shadow-none"
        style={{ borderLeft: `6px solid ${accent}` }}
        data-testid="public-quote-page">
        <Hero doc={doc} accent={accent} />

        {/* intro */}
        <div className="px-5 sm:px-8 pt-8 sm:pt-10 pb-7">
          <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: "var(--proposal-accent-text)" }}>{doc.label}</div>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight proposal-text leading-[1.05]"
            data-testid="proposal-title">
            {doc.title || `${doc.label} for ${client.name || ""}`}
          </h2>
          {doc.description && (
            <p className="mt-3 text-sm sm:text-base proposal-muted max-w-3xl" data-testid="proposal-description">{doc.description}</p>
          )}

          {/* prepared for / details */}
          <div className="mt-8 grid md:grid-cols-2 gap-8 md:gap-10">
            <div data-testid="proposal-prepared-for">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] proposal-muted">Prepared for</div>
              <div className="mt-3 flex items-start gap-3">
                <User className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--proposal-accent-text)" }} strokeWidth={2.2} />
                <div className="min-w-0">
                  <div className="text-lg sm:text-xl font-black tracking-tight proposal-text">{client.name}</div>
                  {client.address && (
                    <div className="mt-1 text-sm proposal-muted whitespace-pre-line leading-relaxed">{client.address}</div>
                  )}
                </div>
              </div>
            </div>
            {details.length > 0 && (
              <div className="md:pl-10 md:border-l proposal-border" data-testid="proposal-details">
                <div className="text-[10px] font-black uppercase tracking-[0.2em] proposal-muted">{doc.label} details</div>
                <dl className="mt-3 space-y-2.5">
                  {details.map((r) => (
                    <div key={r.label} className="flex items-center gap-3">
                      <r.icon className="w-4 h-4 proposal-muted shrink-0" strokeWidth={2.2} />
                      <dt className="text-sm proposal-muted flex-1 min-w-0">{r.label}</dt>
                      <dd className="text-sm font-bold proposal-text text-right">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>

        {/* scope */}
        {(doc.scope_html || (doc.scope_blocks || []).length > 0) && (
          <section className="px-5 sm:px-8 pb-8" data-testid="proposal-scope">
            <div className="proposal-surface border proposal-border">
              <PanelHead icon={ClipboardList} title="Scope of works" accent={accent} testid="scope-head" />
              <div className="px-4 sm:px-6 py-5">
                <Blocks blocks={doc.scope_blocks} html={doc.scope_html} accent={accent} testid="scope-body" />
              </div>
            </div>
          </section>
        )}

        {/* pricing */}
        <section className="px-5 sm:px-8 pb-2" data-testid="proposal-pricing">
          <div className="border proposal-border">
            <PanelHead icon={Hammer} title="Pricing" accent={accent} testid="pricing-head" />
            <PricingTable doc={doc} accent={accent} />
          </div>
        </section>

        <TotalsPanel doc={doc} accent={accent} />

        {/* assumptions / exclusions */}
        {(doc.assumptions?.length > 0 || doc.exclusions?.length > 0) && (
          <section className="px-5 sm:px-8 pt-6 grid md:grid-cols-2 gap-5" data-testid="proposal-assumptions-exclusions">
            {doc.assumptions?.length > 0 && (
              <div className="proposal-surface border proposal-border">
                <PanelHead icon={ClipboardList} title="Assumptions" accent={accent} testid="assumptions-head" />
                <div className="px-4 sm:px-6 py-5"><Blocks blocks={doc.assumptions} accent={accent} /></div>
              </div>
            )}
            {doc.exclusions?.length > 0 && (
              <div className="proposal-surface border proposal-border">
                <PanelHead icon={X} title="Exclusions" accent={accent} testid="exclusions-head" />
                <div className="px-4 sm:px-6 py-5"><Blocks blocks={doc.exclusions} accent={accent} /></div>
              </div>
            )}
          </section>
        )}

        {/* terms */}
        {(terms.length > 0 || doc.payment_terms || org.phone || org.email) && (
          <section className="px-5 sm:px-8 pt-7 pb-8" data-testid="proposal-terms">
            <div className="proposal-surface border proposal-border">
              <PanelHead icon={FileText} title="Terms & conditions" accent={accent} testid="terms-head" />
              <div className="grid md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-8 px-4 sm:px-6 py-5">
                <div>
                  <div className="proposal-screen-only"><Blocks blocks={visibleTerms} accent={accent} testid="public-quote-terms" /></div>
                  <div className="proposal-print-only"><Blocks blocks={terms} accent={accent} testid="proposal-print-terms" /></div>
                  {longTerms && (
                    <button type="button" onClick={() => setShowAllTerms((v) => !v)}
                      className="mt-4 text-[11px] font-black uppercase tracking-[0.14em] underline decoration-2 underline-offset-4 print:hidden"
                      style={{ color: "var(--proposal-accent-text)" }} data-testid="terms-toggle">
                      {showAllTerms ? "Hide full terms & conditions" : "View full terms & conditions"}
                    </button>
                  )}
                </div>
                <div className="space-y-5 md:border-l proposal-border md:pl-8">
                  {doc.payment_terms && (
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] proposal-muted">
                        <CreditCard className="w-4 h-4" style={{ color: "var(--proposal-accent-text)" }} strokeWidth={2.2} /> Payment terms
                      </div>
                      <div className="mt-1.5 text-sm font-semibold proposal-text">{doc.payment_terms}</div>
                    </div>
                  )}
                  {(org.phone || org.email) && (
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] proposal-muted">
                        <MessageSquare className="w-4 h-4" style={{ color: "var(--proposal-accent-text)" }} strokeWidth={2.2} /> Questions?
                      </div>
                      <div className="mt-1.5 text-sm proposal-body leading-relaxed">
                        If you have any questions about this {doc.label?.toLowerCase()}, please contact us on{" "}
                        {org.phone && <a href={`tel:${org.phone.replace(/\s/g, "")}`} className="font-semibold underline decoration-1 underline-offset-2">{org.phone}</a>}
                        {org.phone && org.email ? " or " : ""}
                        {org.email && <a href={`mailto:${org.email}`} className="font-semibold underline decoration-1 underline-offset-2 break-all">{org.email}</a>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {closed
          ? <ClosedPanel doc={doc} accent={accent} />
          : <AcceptancePanel doc={doc} accent={accent} onAccept={onAccept} onDecline={onDecline} busy={busy} preview={preview} />}

        {!closed && <div className="proposal-print-only proposal-print-acceptance">
          <strong>To accept this quote</strong> — open your digital quote using the link in the email provided, then enter your name and accept online.
        </div>}

        {/* footer */}
        <footer className="px-5 sm:px-8 py-6 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="proposal-footer">
          <div className="min-w-0">
            <div className="text-sm font-black proposal-text">{org.name}</div>
            <div className="mt-1 text-[11px] proposal-muted flex flex-wrap gap-x-3 gap-y-1">
              {org.gst_number && <span>GST {org.gst_number}</span>}
              {org.nzbn && <span>NZBN {org.nzbn}</span>}
              {org.address && <span>{org.address.replace(/\n/g, ", ")}</span>}
              {org.phone && <span>{org.phone}</span>}
              {org.email && <span className="break-all">{org.email}</span>}
              {org.website && (
                <a href={org.website.startsWith("http") ? org.website : `https://${org.website}`}
                  target="_blank" rel="noopener noreferrer" className="underline">{org.website}</a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-8">
            {(org.socials || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] proposal-muted">Follow us</div>
                <div className="mt-1.5 flex items-center gap-2">
                  {org.socials.map((s) => {
                    const Icon = s.kind === "linkedin" ? Linkedin : s.kind === "facebook" ? Facebook : Instagram;
                    return (
                      <a key={s.kind} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.kind}
                        className="w-7 h-7 flex items-center justify-center proposal-heading-surface text-white hover:opacity-80 transition-opacity">
                        <Icon className="w-3.5 h-3.5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            {!doc.hide_fieldlink_branding && <div className="shrink-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] proposal-muted">Powered by</div>
              <a href="https://fieldlink.co.nz" target="_blank" rel="noopener noreferrer"
                className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-tight proposal-text">
                <span className="w-5 h-5 flex items-center justify-center bg-[#FF5F15] text-white text-[9px] font-black">FL</span>
                Fieldlink
              </a>
            </div>}
          </div>
        </footer>
      </article>
    </div>
  );
}
